import {
  SidekickDockShell,
  useDockSuggestions,
  useIsSidekickEnabled,
  useIsSidekickM1Enabled,
} from '@brightdrop/ai-sidekick';
import { getSidekickApiBaseUrl } from '@brightdrop/ai-sidekick/api';
import { useSidekickHeapEvents } from '@brightdrop/ai-sidekick/heap';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useEntitlements } from '@gm-commercial/entitlements-client';
import { Role } from '@gm-commercial/profile-model';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SIDEKICK_PUSH_MIN_VIEWPORT_PX } from '~/common/constants/sidekick.constants';
import useActingOrganizationId from '~/common/hooks/useActingOrganizationId';
import useCurrentContext from '~/common/hooks/useCurrentContext';
import { CATALOG_FLAG } from '~/common/models/featureFlags.model';
import { FeatureCodes } from '~/common/models/plans.model';
import { RouteName } from '~/common/models/route.model';
import { getHeapPlatform } from '~/common/utils/analytics/heap.utils';
import { makeRoutePathLite } from '~/common/utils/route/route.utils';
import { HEADER_HEIGHT } from '~/features/header/GlobalHeader.styles';
import {
  selectActingProfileOrganization,
  selectActingRole,
  selectCurrentLocale,
  selectProfileId,
} from '~/store/profile/profileSlice.selectors';
import { SIDEKICK_INTERFACE_NAME } from './constants';
import { useIsSidekickDockRoute } from './useIsSidekickDockRoute';
import useShareRecipientSearch from './useShareRecipientSearch';
import useSidekickResultNavigate from './useSidekickResultNavigate';

/**
 * Mounts the docked Fleet AI Assistant for the CSP host.
 *
 * Owns everything the package deliberately does not: the telematics
 * entitlement, the role check behind the plan CTA, and the catalog
 * route. Entitlements and routes are app concerns, so they are
 * resolved here and handed down as plain props.
 *
 * The shell self-gates on the Sidekick feature flags, so callers need
 * not; the flags are read here only to keep the suggestions request
 * from firing when nothing will be shown. Route eligibility is the one
 * gate the package cannot make, since it depends on which app chrome
 * the current route renders.
 */
function SidekickDockMount(): JSX.Element | null {
  const navigate = useNavigate();
  const isDockRoute = useIsSidekickDockRoute();
  const { getFlag } = useFeatureFlags();
  const isSidekickEnabled = useIsSidekickEnabled();
  const isSidekickM1Enabled = useIsSidekickM1Enabled();
  const {
    hasFeature,
    isReady: isEntitlementsReady,
    isLoading: isEntitlementsLoading,
  } = useEntitlements();
  const role = useSelector(selectActingRole);
  const userId = useSelector(selectProfileId);
  const locale = useSelector(selectCurrentLocale);
  const selectedContext = useCurrentContext();
  const fleetId = selectedContext?.fleetsId;
  const actingOrganization = useSelector(selectActingProfileOrganization);
  const organizationsId = actingOrganization?.id;
  const actingOrganizationId = useActingOrganizationId();
  const searchRecipients = useShareRecipientSearch();
  const handleResultNavigate = useSidekickResultNavigate();

  // `hasFeature` reads false until the entitlements fetch settles, so
  // the answer is only the account's own once `isReady`.
  const hasTelematicsPlan =
    isEntitlementsReady && hasFeature(FeatureCodes.TELE);
  // Only an in-flight fetch is worth waiting on. Entitlements are
  // fetched once at bootstrap and only for a profile that has an
  // acting organization, so a profile without one — or one activated
  // after that fetch would have run — never leaves the unstarted
  // state. Treating unstarted as pending would hold the card's
  // skeleton, and with it the composer, for the whole session; it
  // resolves like a failed fetch instead, to the upsell.
  const isTelematicsPlanLoading = isEntitlementsLoading;
  const isDockVisible = isDockRoute && isSidekickEnabled && isSidekickM1Enabled;

  // An account without telematics sees the upsell instead of chips, so
  // there is nothing to fetch on that branch.
  const { chips, isLoading } = useDockSuggestions({
    skip: !isDockVisible || !hasTelematicsPlan,
  });

  // Only account managers can shop plans, and only when the catalog is
  // live — otherwise the upsell states the reason without a dead end.
  // The catalog lives under an organization, and `organization` is
  // optional on a profile, so without one the CTA would offer a route
  // it cannot build.
  const showExplorePlansCta =
    role === Role.ORG_MANAGER &&
    getFlag(CATALOG_FLAG) === true &&
    !!organizationsId;

  const handleExplorePlans = useCallback(() => {
    navigate(
      makeRoutePathLite({
        routeName: RouteName.SHOP_PLANS,
        params: { organizationsId },
      })
    );
  }, [navigate, organizationsId]);

  const { onArtifactDownloaded, onLifecycleEvent, onFeatureUsed } =
    useSidekickHeapEvents({
      organizationId: organizationsId,
      organizationName: actingOrganization?.name,
      fleetId,
      isEmbedded: true,
      platform: getHeapPlatform(),
    });

  const appApiBaseUrl = globalThis.appConfig?.apiBaseUrl;

  if (!isDockRoute) {
    return null;
  }

  return (
    <SidekickDockShell
      apiBaseUrl={getSidekickApiBaseUrl(
        typeof appApiBaseUrl === 'string' ? appApiBaseUrl : ''
      )}
      chips={chips}
      isChipsLoading={isLoading}
      hasTelematicsPlan={hasTelematicsPlan}
      isTelematicsPlanLoading={isTelematicsPlanLoading}
      showExplorePlansCta={showExplorePlansCta}
      onExplorePlans={handleExplorePlans}
      onNavigate={handleResultNavigate}
      fleetId={fleetId}
      interfaceName={SIDEKICK_INTERFACE_NAME}
      actingOrganizationId={actingOrganizationId}
      userId={userId}
      locale={locale}
      topOffsetPx={HEADER_HEIGHT}
      overlayBelowPx={SIDEKICK_PUSH_MIN_VIEWPORT_PX}
      onArtifactDownloaded={onArtifactDownloaded}
      onLifecycleEvent={onLifecycleEvent}
      onFeatureUsed={onFeatureUsed}
      searchRecipients={searchRecipients}
    />
  );
}

export default SidekickDockMount;
